import {
  IntegrationSettingsSchema,
  IntegrationVersion,
  IntegrationVersionSchema,
  type IntegrationProviders,
  type IntegrationSettings,
} from '@vy/types';
import { type IntegrationsSettings, IntegrationsSettingsSchema } from '@vy/integrations-types';
import { useQuery } from 'react-query';
import { getPrivateApi } from '../../services/api';
import { useActivatedIntegration } from '../use-activated-integration';
import { isNotNullOrUndefined, Nullable, validateNotNullOrUndefined } from '@vy/utils';
import { queryKeys } from '../../services/query-key/query-keys';

type IntegrationSettingsResponse = {
  integrationId: string;
  integrationSettings: IntegrationSettings | IntegrationsSettings;
};

export function useIntegrationSettings(provider: Nullable<IntegrationProviders>) {
  const isIntegrationConnected = useActivatedIntegration(provider, { suspense: true });

  return useQuery<IntegrationSettingsResponse>({
    queryKey: queryKeys.integrationSettings(provider),
    queryFn: async () => {
      const privateApi = getPrivateApi();

      validateNotNullOrUndefined(provider, 'provider');

      const { integrationSettings, integrationId } = await privateApi.getIntegrationsSettings({
        provider,
      });

      const parsedSettings = isVersionV3Integration(integrationSettings.version)
        ? IntegrationsSettingsSchema.parse(integrationSettings.settings)
        : IntegrationSettingsSchema.parse(integrationSettings.settings);

      return { integrationId, integrationSettings: parsedSettings };
    },
    enabled: isNotNullOrUndefined(provider) && isIntegrationConnected,
    suspense: true,
  });
}

function isVersionV3Integration(integrationVersion: IntegrationVersion): boolean {
  return integrationVersion === IntegrationVersionSchema.v3;
}
